import { useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";

import type { AccountingPeriod } from "../../services/financialService";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

interface Props {
  periods?: AccountingPeriod[];
  isLoading?: boolean;
  currentUserId?: number;
  canConfigure: boolean;
  canClose: boolean;
  canRequestReopen: boolean;
  canReview: boolean;
  canReopen: boolean;
  /** `done` runs once the period is saved, so a refused one keeps what was typed. */
  onCreate: (data: { name: string; start_date: string; end_date: string }, done: () => void) => void;
  onClose: (periodId: number) => void;
  onRequestReopen: (data: { id: number; reason: string }) => void;
  onVerifyReopen: (data: { id: number; comment?: string }) => void;
  onReturnReopen: (data: { id: number; reason: string }) => void;
  onRejectReopen: (data: { id: number; reason: string }) => void;
  onApproveReopen: (requestId: number) => void;
}

const REQUEST_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Awaiting review",
  verified: "Awaiting approval",
  returned: "Returned",
  approved: "Reopened",
  rejected: "Rejected",
};

const requestPending = (period: AccountingPeriod) =>
  ["submitted", "verified"].includes(period.latest_reopen_request?.workflow_status ?? "");

const lengthInDays = (period: AccountingPeriod) =>
  Math.round((Date.parse(period.end_date) - Date.parse(period.start_date)) / 86_400_000) + 1;

const periodStatus = (period: AccountingPeriod) =>
  period.is_closed ? { label: "Closed", dot: "bg-gray-400" } : { label: "Open", dot: "bg-emerald-500" };

const config: RegisterConfig<AccountingPeriod> = {
  search: (period) => [period.name, period.latest_reopen_request?.reason].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "open", label: "Open", match: (period) => !period.is_closed },
    { key: "closed", label: "Closed", match: (period) => period.is_closed },
    { key: "reopen", label: "Reopen requested", match: requestPending },
  ],
  date: { label: "Starts", value: (period) => period.start_date },
  sorts: [
    { key: "-start", label: "Newest first", compare: reversed(byText<AccountingPeriod>((period) => period.start_date)) },
    { key: "start", label: "Oldest first", compare: byText<AccountingPeriod>((period) => period.start_date) },
    { key: "name", label: "Name (A–Z)", compare: byText<AccountingPeriod>((period) => period.name) },
  ],
};

const columns: RegisterColumn<AccountingPeriod>[] = [
  { header: "Period", cell: (period) => <span className="font-medium text-gray-900">{period.name}</span>, exportValue: (period) => period.name },
  { header: "Starts", cell: (period) => period.start_date, exportValue: (period) => period.start_date, className: "tabular-nums text-gray-700" },
  { header: "Ends", cell: (period) => period.end_date, exportValue: (period) => period.end_date, className: "tabular-nums text-gray-700" },
  { header: "Days", cell: lengthInDays, exportValue: lengthInDays, align: "right" },
  { header: "Status", cell: (period) => <StatusDot {...periodStatus(period)} />, exportValue: (period) => periodStatus(period).label },
  {
    header: "Reopen request",
    cell: (period) => {
      const status = period.latest_reopen_request?.workflow_status;
      if (!status) return <span className="text-gray-300">—</span>;
      return <span className={requestPending(period) ? "font-medium text-amber-700" : "text-gray-600"}>{REQUEST_LABELS[status] ?? humanise(status)}</span>;
    },
    exportValue: (period) => {
      const status = period.latest_reopen_request?.workflow_status;
      return status ? REQUEST_LABELS[status] ?? humanise(status) : "";
    },
  },
  {
    header: "Last reopened",
    cell: (period) => period.reopened_at?.slice(0, 10) ?? <span className="text-gray-300">—</span>,
    exportValue: (period) => period.reopened_at?.slice(0, 10) ?? "",
    className: "tabular-nums text-gray-600",
  },
];

/**
 * Accounting periods: create, close and, through a separate request, review
 * and approval, reopen. Every reason is written with the assistant beside it.
 */
export default function AccountingPeriodsPanel({
  periods,
  isLoading,
  currentUserId,
  canConfigure,
  canClose,
  canRequestReopen,
  canReview,
  canReopen,
  onCreate,
  onClose,
  onRequestReopen,
  onVerifyReopen,
  onReturnReopen,
  onRejectReopen,
  onApproveReopen,
}: Props) {
  const [showForm, setShowForm] = useState(false);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [reopenReason, setReopenReason] = useState("");
  const [decisionNotes, setDecisionNotes] = useState("");
  const list = periods ?? [];
  const viewing = list.find((period) => period.id === viewingId) ?? null;

  const open = (period: AccountingPeriod) => {
    setReopenReason("");
    setDecisionNotes("");
    setViewingId(period.id);
  };
  const close = () => setViewingId(null);

  const submitPeriod = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    onCreate(
      { name: String(data.get("name")), start_date: String(data.get("start")), end_date: String(data.get("end")) },
      () => {
        form.reset();
        setShowForm(false);
      },
    );
  };

  // What this viewer may do with a period's reopening request.
  const canActOn = (period: AccountingPeriod) => {
    const request = period.latest_reopen_request;
    const independent = request ? request.initiated_by !== currentUserId : false;
    const pending = requestPending(period);
    return {
      request,
      verify: Boolean(request && independent && request.workflow_status === "submitted" && canReview),
      sendBack: Boolean(request && independent && pending && canReview),
      approve: Boolean(request && independent && pending && canReopen),
      reject: Boolean(request && independent && pending && canReopen),
    };
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
        <div>
          <h2 className="font-semibold text-primary-950">Accounting periods</h2>
          <p className="mt-1 max-w-2xl text-sm text-primary-800">
            Closing a period stops anything posting into its dates. Reopening one needs a request, a review and an approval.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{list.filter((period) => !period.is_closed).length} open</span>
          <span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{list.filter((period) => period.is_closed).length} closed</span>
          <span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{list.filter(requestPending).length} reopen requests</span>
        </div>
      </div>

      {canConfigure && (showForm || (!isLoading && !list.length)) && (
        <form onSubmit={submitPeriod} className="card">
          <div className="card-titlebar">
            <div>
              <h3 className="font-semibold text-gray-900">New accounting period</h3>
              <p className="mt-1 text-sm text-gray-500">Usually one per term or per month. Periods should not overlap.</p>
            </div>
            {list.length > 0 && (
              <button type="button" onClick={() => setShowForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the new period form">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="card-content pt-5">
            <FormSection title="Period" divided={false} columns={3}>
              <Field label="Name">
                <input required name="name" className="input mt-1" placeholder="e.g. Term 3 2026" />
              </Field>
              <Field label="Starts">
                <input required name="start" type="date" className="input mt-1" />
              </Field>
              <Field label="Ends">
                <input required name="end" type="date" className="input mt-1" />
              </Field>
            </FormSection>
          </div>
          <div className="card-footer">
            <p className="mr-auto text-xs text-gray-500">A new period starts open.</p>
            <button className="btn btn-primary">Create period</button>
          </div>
        </form>
      )}

      <ClientRegister
        title="Periods"
        description="Open a period to close it, request its reopening, or decide a request."
        actions={canConfigure && !showForm && list.length > 0 && (
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New period
          </button>
        )}
        rows={periods}
        config={config}
        columns={columns}
        rowKey={(period) => period.id}
        onOpen={open}
        openLabel={(period) => {
          const can = canActOn(period);
          return can.verify || can.approve ? "Review" : "Open";
        }}
        noun={["period", "periods"]}
        exportName={{ file: "accounting-periods", title: "Accounting Periods" }}
        isLoading={isLoading}
        emptyText="No accounting periods yet."
      />

      {viewing && (() => {
        const period = viewing;
        const can = canActOn(period);
        const request = can.request;
        const pending = requestPending(period);
        const decisionPurpose = can.reject ? "reject" : "return";
        const notes = decisionNotes.trim();
        return (
          <DetailsModal
            title={period.name}
            headline={{ label: "Accounting period", value: `${period.start_date} → ${period.end_date}`, note: `${lengthInDays(period)} days` }}
            status={periodStatus(period)}
            facts={[
              ["Starts", period.start_date],
              ["Ends", period.end_date],
              ["Closed on", period.closed_at?.slice(0, 10)],
              ["Closed by", period.closed_by_name],
              ["Last reopened", period.reopened_at?.slice(0, 10)],
              ["Reopened by", period.reopened_by_name],
            ]}
            onClose={close}
          >
            {canClose && !period.is_closed && (
              <ActionPanel title="Close this period" description="Nothing can then post into these dates until a reopening is approved.">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Close ${period.name}? Postings dated ${period.start_date} to ${period.end_date} will be blocked.`)) {
                        onClose(period.id);
                      }
                    }}
                    className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Close period
                  </button>
                </div>
              </ActionPanel>
            )}

            {period.is_closed && canRequestReopen && !pending && (
              <ActionPanel title="Request reopening" description="Say what late transaction or correction needs the period open, and the document behind it.">
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (reopenReason.trim()) {
                      onRequestReopen({ id: period.id, reason: reopenReason.trim() });
                      setReopenReason("");
                    }
                  }}
                  className="space-y-3"
                >
                  <Field as="div" label="Reason and supporting reference">
                    <AIAssistField
                      name="reason"
                      rows={3}
                      value={reopenReason}
                      onChange={(next) => setReopenReason(next)}
                      placeholder="e.g. Supplier invoice INV-5521 for July fuel arrived after closing; post it in Term 2."
                      context={{ key: "financial.period_reopen_reason", params: { period: period.id } }}
                      draftLabel="Draft the reason"
                      draftHint="Reads this period. Jot the transaction and document first, and it writes them up."
                    />
                  </Field>
                  <div className="flex justify-end">
                    <button disabled={!reopenReason.trim()} className="btn btn-primary">Send reopening request</button>
                  </div>
                </form>
              </ActionPanel>
            )}

            {request && (
              <section className="space-y-3 rounded-lg border border-gray-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-gray-900">Latest reopening request</h4>
                  <span className="text-xs font-medium text-gray-600">
                    {REQUEST_LABELS[request.workflow_status ?? ""] ?? humanise(request.workflow_status)}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Requested by {request.initiated_by === currentUserId ? "you" : request.initiated_by_name}
                  {request.initiated_at ? ` on ${request.initiated_at.slice(0, 10)}` : ""}
                  {request.reviewed_by_name ? ` · reviewed by ${request.reviewed_by_name}` : ""}
                  {request.approved_by_name ? ` · approved by ${request.approved_by_name}` : ""}
                </p>
                <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{request.reason}</p>
                {request.return_reason && request.workflow_status === "returned" && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">Sent back: {request.return_reason}</p>
                )}
                {request.rejection_reason && request.workflow_status === "rejected" && (
                  <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">Rejected: {request.rejection_reason}</p>
                )}
                {pending && !(can.verify || can.sendBack || can.approve) && request.initiated_by === currentUserId && (
                  <p className="text-sm text-gray-500">You asked for this, so someone else reviews and approves it.</p>
                )}

                {(can.verify || can.sendBack || can.approve || can.reject) && (
                  <div className="space-y-3 border-t border-gray-100 pt-3">
                    <Field as="div" label="Comments" hint="Needed to send it back or reject it, so the requester knows why.">
                      <AIAssistField
                        rows={2}
                        value={decisionNotes}
                        onChange={(next) => setDecisionNotes(next)}
                        context={{
                          key: "financial.approval_note",
                          params: { document_type: "financial.accountingperiodreopenrequest", id: request.id, purpose: decisionPurpose },
                        }}
                        draftLabel={decisionPurpose === "reject" ? "Draft a reason" : "Draft what to change"}
                        draftHint="Reads this request. Jot rough notes first for a better draft."
                      />
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      {can.reject && (
                        <button
                          type="button"
                          disabled={!notes}
                          title={notes ? undefined : "Write why before rejecting"}
                          onClick={() => {
                            onRejectReopen({ id: request.id, reason: notes });
                            setDecisionNotes("");
                          }}
                          className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      )}
                      {can.sendBack && (
                        <button
                          type="button"
                          disabled={!notes}
                          title={notes ? undefined : "Write what needs to change"}
                          onClick={() => {
                            onReturnReopen({ id: request.id, reason: notes });
                            setDecisionNotes("");
                          }}
                          className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                        >
                          Send back
                        </button>
                      )}
                      {can.verify && (
                        <button type="button" onClick={() => onVerifyReopen({ id: request.id, comment: notes || undefined })} className="btn btn-primary">
                          Mark reviewed
                        </button>
                      )}
                      {can.approve && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Reopen ${period.name}? Postings into ${period.start_date} to ${period.end_date} will be allowed again.`)) {
                              onApproveReopen(request.id);
                            }
                          }}
                          className="btn btn-primary"
                        >
                          Approve reopening
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </section>
            )}
          </DetailsModal>
        );
      })()}
    </div>
  );
}
